import { useState, useEffect, useCallback } from 'react';
import { 
  StockDataPoint, 
  MetricKey, 
  VolatilityResponse, 
  GammaExposurePoint, 
  GammaMetricKey, 
  FlowPerStrikePoint, 
  FlowMetricKey,
  LdpmHistoryPoint,
  CandleSize,
  Timeframe,
  LdpmMetricKey,
  OpenInterestChange,
  OiSortKey,
  OiFilterType
} from '../types/StockData';

export const useStockData = (currentTicker: string, windowSize: number) => {
  const [data, setData] = useState<StockDataPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedDataPoint, setSelectedDataPoint] = useState<StockDataPoint | null>(null);

  const loadStockData = async (ticker: string, window: number) => {
    setLoading(true);
    setError('');
    
    try {
      const apiUrl = `https://tradewithaiapi20250728175655.azurewebsites.net/api/whales/flowsnapshots/${ticker}/${window}`;
      
      console.log(`Fetching data from: ${apiUrl}`);
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const apiData = await response.json();
      console.log('API Response:', apiData);
      
      // Sort data by timestamp to ensure proper chronological order
      const sortedData = (apiData as StockDataPoint[]).sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      setData(sortedData);
      // Set the latest data point as default selected
      if (sortedData.length > 0) {
        setSelectedDataPoint(sortedData[sortedData.length - 1]);
      }
    } catch (err) {
      setError('Failed to load stock data from API');
      console.error('Error loading stock data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStockData(currentTicker, windowSize);
  }, [currentTicker, windowSize]);

  return {
    data,
    loading,
    error,
    selectedDataPoint,
    setSelectedDataPoint,
    loadStockData
  };
};

export const useVolatilityData = (currentTicker: string, activeTab: string) => {
  const [volatilityData, setVolatilityData] = useState<VolatilityResponse | null>(null);
  const [volatilityLoading, setVolatilityLoading] = useState<boolean>(false);

  const loadVolatilityData = async (ticker: string) => {
    setVolatilityLoading(true);
    
    try {
      const apiUrl = `https://tradewithaiapi20250728175655.azurewebsites.net/api/whales/volstats/${ticker}`;
      
      console.log(`Fetching volatility data from: ${apiUrl}`);
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const volData = await response.json();
      console.log('Volatility API Response:', volData);
      
      setVolatilityData(volData as VolatilityResponse);
    } catch (err) {
      console.error('Error loading volatility data:', err);
      setVolatilityData(null);
    } finally {
      setVolatilityLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'volatility') {
      loadVolatilityData(currentTicker);
    }
  }, [currentTicker, activeTab]);

  return {
    volatilityData,
    volatilityLoading,
    loadVolatilityData
  };
};

export const useGammaData = (currentTicker: string, activeTab: string, selectedDataPoint: StockDataPoint | null, data: StockDataPoint[]) => {
  const [gammaData, setGammaData] = useState<GammaExposurePoint[]>([]);
  const [gammaLoading, setGammaLoading] = useState(false);

  const loadGammaData = async (ticker: string) => {
    setGammaLoading(true);
    
    try {
      // Get current price for strike range calculation
      const currentPrice = selectedDataPoint?.price || data[data.length - 1]?.price || 180; // fallback price
      const minStrike = Math.round(currentPrice * 0.95);
      const maxStrike = Math.round(currentPrice * 1.05);
      
      const apiUrl = `https://tradewithaiapi20250728175655.azurewebsites.net/api/whales/GammaExposure/${ticker}?MaxDte=5&MinStrike=${minStrike}&MaxStrike=${maxStrike}`;
      
      console.log(`Fetching gamma data from: ${apiUrl}`);
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const gammaApiData = await response.json();
      console.log('Gamma API Response:', gammaApiData);
      
      setGammaData(gammaApiData as GammaExposurePoint[]);
    } catch (err) {
      console.error('Error loading gamma data:', err);
      setGammaData([]);
    } finally {
      setGammaLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'gamma') {
      loadGammaData(currentTicker);
    }
  }, [currentTicker, activeTab]);

  return {
    gammaData,
    gammaLoading,
    loadGammaData
  };
};

export const useFlowPerStrikeData = (currentTicker: string, activeTab: string, selectedDataPoint: StockDataPoint | null, data: StockDataPoint[]) => {
  const [flowPerStrikeData, setFlowPerStrikeData] = useState<FlowPerStrikePoint[]>([]);
  const [flowPerStrikeLoading, setFlowPerStrikeLoading] = useState(false);

  const loadFlowPerStrikeData = async (ticker: string) => {
    setFlowPerStrikeLoading(true);
    try {
      const currentPrice = selectedDataPoint?.price || data[data.length - 1]?.price || 180;
      const minStrike = Math.round(currentPrice * 0.95);
      const maxStrike = Math.round(currentPrice * 1.05);
      
      console.log(`Loading flow per strike data for ${ticker} with strikes ${minStrike}-${maxStrike}`);
      
      const apiUrl = `https://tradewithaiapi20250728175655.azurewebsites.net/api/whales/FlowPerStrike/${ticker}?MinStrike=${minStrike}&MaxStrike=${maxStrike}`;
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const flowApiData = await response.json();
      console.log('Flow per strike API response:', flowApiData);
      
      setFlowPerStrikeData(flowApiData as FlowPerStrikePoint[]);
    } catch (err) {
      console.error('Error loading flow per strike data:', err);
      setFlowPerStrikeData([]);
    } finally {
      setFlowPerStrikeLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'flowPerStrike') {
      loadFlowPerStrikeData(currentTicker);
    }
  }, [currentTicker, activeTab]);

  return {
    flowPerStrikeData,
    flowPerStrikeLoading,
    loadFlowPerStrikeData
  };
};

export const useLdpmHistoryData = (ticker: string, candleSize: CandleSize, timeframe: Timeframe) => {
  const [ldpmHistoryData, setLdpmHistoryData] = useState<LdpmHistoryPoint[]>([]);
  const [ldpmHistoryLoading, setLdpmHistoryLoading] = useState(false);
  const [ldpmHistoryError, setLdpmHistoryError] = useState<string>('');

  const loadLdpmHistoryData = async (tickerSymbol: string, candle: CandleSize, tf: Timeframe) => {
    setLdpmHistoryLoading(true);
    setLdpmHistoryError('');
    
    try {
      const apiUrl = `https://tradewithaiapi20250728175655.azurewebsites.net/api/whales/ldpm/${tickerSymbol}/${candle}?timeframe=${tf}`;
      
      console.log(`Fetching LDPM history data from: ${apiUrl}`);
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const ldpmData = await response.json();
      console.log('LDPM History API Response:', ldpmData);
      
      // Sort data by timestamp to ensure proper chronological order
      const sortedData = (ldpmData as LdpmHistoryPoint[]).sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      setLdpmHistoryData(sortedData);
    } catch (err) {
      setLdpmHistoryError('Failed to load LDPM history data from API');
      console.error('Error loading LDPM history data:', err);
    } finally {
      setLdpmHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (ticker) {
      loadLdpmHistoryData(ticker, candleSize, timeframe);
    }
  }, [ticker, candleSize, timeframe]);

  return {
    ldpmHistoryData,
    ldpmHistoryLoading,
    ldpmHistoryError,
    loadLdpmHistoryData
  };
};

// Open Interest Changes hook
export const useOpenInterestData = (ticker: string) => {
  const [oiData, setOiData] = useState<OpenInterestChange[]>([]);
  const [oiLoading, setOiLoading] = useState(false);
  const [oiError, setOiError] = useState<string | null>(null);

  const loadOpenInterestData = useCallback(async (tickerSymbol: string) => {
    if (!tickerSymbol) return;
    
    setOiLoading(true);
    setOiError(null);
    
    try {
      console.log(`Loading open interest data for ${tickerSymbol}`);
      const response = await fetch(`https://tradewithaiapi20250728175655.azurewebsites.net/api/whales/oichange/${tickerSymbol.toLowerCase()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: OpenInterestChange[] = await response.json();
      console.log('Open interest data loaded:', data);
      setOiData(data || []);
    } catch (error) {
      console.error('Error loading open interest data:', error);
      setOiError(error instanceof Error ? error.message : 'Failed to load open interest data');
      setOiData([]);
    } finally {
      setOiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ticker) {
      loadOpenInterestData(ticker);
    }
  }, [ticker, loadOpenInterestData]);

  return {
    oiData,
    oiLoading,
    oiError,
    loadOpenInterestData
  };
};
